import { apiClient } from '@/api/core';
import { API_CONFIG } from '@/api/core/apiConfig';
import { buildQueryString } from '@/api/shared/queryBuilder';
import type { ApiResponse, LC_PagedResult } from '@/api/shared';
import type {
  LC_ScheduleListItemOut,
  LC_ScheduleDetailOut,
  LC_ScheduleIn,
  ScheduleListParams,
  ScheduleExportParams,
  SchedulesPage,
  ScheduleDetail,
} from './schedule.types';
import { mapPagedSchedules, mapScheduleDetailOutToModel } from './schedule.mapper';


export class ScheduleService {
  /** Fetch paginated schedule list */
  async list(params: ScheduleListParams): Promise<ApiResponse<SchedulesPage>> {
    const query = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      orderBy: params.orderBy,
      orderDirection: params.orderDirection,
      timeZoneId: params.timeZoneId ?? undefined,
      isTwoDaySchedule: params.isTwoDaySchedule ?? undefined,
    });

    const response = await apiClient.get<LC_PagedResult<LC_ScheduleListItemOut>>(
      `${API_CONFIG.ENDPOINTS.SCHEDULES.LIST}${query}`,
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: mapPagedSchedules(response.data),
      };
    }

    return { success: false, data: null, error: response.error };
  }

  /** Create a new schedule — POST /v{version}/schedules */
  async create(data: LC_ScheduleIn): Promise<ApiResponse<LC_ScheduleDetailOut>> {
    return apiClient.post<LC_ScheduleDetailOut>(API_CONFIG.ENDPOINTS.SCHEDULES.LIST, data);
  }

  /** Update Schedule — PATCH /v{version}/schedules/{scheduleId} */
  async update(
    scheduleId: number,
    data: LC_ScheduleIn,
  ): Promise<ApiResponse<LC_ScheduleDetailOut>> {
    return apiClient.patch<LC_ScheduleDetailOut>(
      `${API_CONFIG.ENDPOINTS.SCHEDULES.LIST}/${scheduleId}`,
      data,
    );
  }

  /** Delete a schedule by id — DELETE /v{version}/schedules/{scheduleId} */
  async delete(scheduleId: number): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`${API_CONFIG.ENDPOINTS.SCHEDULES.LIST}/${scheduleId}`);
  }

  /** Fetch a single schedule by id — GET /v{version}/schedules/{scheduleId} */
  async getById(scheduleId: number): Promise<ApiResponse<ScheduleDetail>> {
    const response = await apiClient.get<LC_ScheduleDetailOut>(`${API_CONFIG.ENDPOINTS.SCHEDULES.LIST}/${scheduleId}`);

    if (response.success && response.data) {
      return { ...response, data: mapScheduleDetailOutToModel(response.data) };
    }

    return { ...response, data: null };
  }

  /** Export schedules to file — GET /v{version}/schedules/file */
  async export(params?: ScheduleExportParams)
    : Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const query = buildQueryString({
      searchText: params?.searchText,
      timeZoneId: params?.timeZoneId ?? undefined,
      isTwoDaySchedule: params?.isTwoDaySchedule ?? undefined,
    });

    return apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.SCHEDULES.FILE}${query}`,
      { method: 'GET' },
    );
  }
}

export const scheduleService = new ScheduleService();
